import React, { useState, useCallback, useEffect } from 'react';
import { Table, Badge, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Atendimento } from '../../types';
import { Sun, Sunset, Calendar, ChevronDown, Eye } from 'lucide-react';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery';
import { getAtendimentosPaginated } from '../../services/atendimentoService';
import { useAuth } from '../../hooks/useAuth';
import { useAtendimentos } from '../../hooks/useAtendimentos';
import { useFuncionarios } from '../../hooks/useFuncionarios';
import AtendimentoForm from './AtendimentoForm';

interface AtendimentoHistoricoProps {
  atendimentos?: Atendimento[]; // Agora opcional, usado apenas para "Em Andamento"
}

const AtendimentoHistorico: React.FC<AtendimentoHistoricoProps> = ({ atendimentos: atendimentosProp }) => {
  const { user } = useAuth();
  const userId = user?.uid || '';

  const [filtroAtendente, setFiltroAtendente] = useState<string>('');
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroPeriodo, setFiltroPeriodo] = useState<string>('hoje');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Estados para edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [atendimentoParaEditar, setAtendimentoParaEditar] = useState<Atendimento | null>(null);
  const [modalKey, setModalKey] = useState(0);

  // Hook de paginação (20 atendimentos por página)
  const fetchAtendimentos = useCallback(
    (lastDoc?: any) => getAtendimentosPaginated(userId, 20, lastDoc),
    [userId]
  );

  const {
    items: atendimentosPaginados,
    loadMore,
    reset,
    hasMore,
    loading: loadingMore
  } = usePaginatedQuery<Atendimento>(fetchAtendimentos);

  // Hooks para edição
  const { atualizar } = useAtendimentos(userId);
  const { funcionarios } = useFuncionarios(userId);

  // Carregar primeira página ao montar (apenas UMA vez)
  useEffect(() => {
    if (userId && !isInitialized) {
      setIsInitialized(true);
      reset();
    }
  }, [userId]); // Removido reset das dependências para evitar loop

  // Função para abrir edição
  const handleEditAtendimento = (atendimento: Atendimento) => {
    setAtendimentoParaEditar(atendimento);
    setModalKey(prev => prev + 1); // Força recriação do modal
    setShowEditModal(true);
  };

  // Função para salvar edição (não atualiza hora de início)
  const handleSaveEditAtendimento = async (data: any) => {
    try {
      if (!atendimentoParaEditar) return;
      
      const funcionario = funcionarios.find(f => f.id === data.funcionarioId);
      if (!funcionario) throw new Error('Funcionário não encontrado');

      // Atualizar apenas os campos permitidos
      const updateData: any = {
        clienteNome: data.clienteNome,
        funcionarioId: data.funcionarioId,
        funcionarioNome: funcionario.nome,
        tipoAtendimento: data.tipoAtendimento,
        observacoes: data.observacoes || ''
        // NÃO incluir horaInicio - mantém a original
      };

      // Adicionar resultado apenas se tiver valor
      if (data.resultado && data.resultado.trim()) {
        updateData.resultado = data.resultado;
      }

      // Se clienteId está vazio (cliente novo), remover o campo do banco
      if (data.clienteId && data.clienteId.trim()) {
        updateData.clienteId = data.clienteId;
      } else {
        // Para cliente novo, remover o clienteId do banco
        updateData.clienteId = null;
      }

      const resultado = await atualizar(atendimentoParaEditar.id!, updateData);
      
      if (resultado.success) {
        // Aguardar um pouco para garantir que o Firebase processou
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // Recarregar dados do histórico ANTES de fechar o modal
        await reset();
        
        // Fechar modal depois de recarregar
        setShowEditModal(false);
        setAtendimentoParaEditar(null);
      }
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      alert('Erro ao atualizar atendimento');
    }
  };

  // Usar atendimentos paginados do Firebase (para histórico completo)
  const atendimentos = atendimentosPaginados;

  const getTipoAtendimentoLabel = (tipo: string): string => {
    const tipos: { [key: string]: string } = {
      'atendimento_escolher': 'ATENDIMENTO ESCOLHER',
      'atendimento_escolher_entrega': 'ATENDIMENTO ESCOLHER ENTREGA',
      'entrega': 'ENTREGA',
      'entrega_marcar_barra': 'ENTREGA/MARCAR BARRA',
      'marcar_barra': 'MARCAR BARRA',
      'escolher_infantil': 'ESCOLHER/INFANTIL',
      'escolher': 'ESCOLHER',
      'orcamento': 'ORÇAMENTO'
    };

    return tipos[tipo] || tipo.toUpperCase();
  };

  const getResultadoLabel = (resultado?: string): string => {
    const resultados: { [key: string]: string } = {
      'finalizou_contrato': 'FINALIZOU O CONTRATO',
      'nao_pagou': 'NÃO FEZ O PAGAMENTO AINDA',
      'nao_levou': 'NÃO LEVOU',
      'so_orcamento': 'SÓ ORÇAMENTO',
      'converteu_reserva': 'CONVERTEU EM RESERVA',
      'outros': 'OUTROS'
    };

    return resultados[resultado || ''] || resultado?.toUpperCase() || '-';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'finalizado':
        return <Badge bg="success">Finalizado</Badge>;
      case 'em_andamento':
        return <Badge bg="warning">Em Andamento</Badge>;
      case 'cancelado':
        return <Badge bg="danger">Cancelado</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Filtrar atendimentos
  const atendimentosFiltrados = atendimentos.filter(atend => {
    // Filtro por atendente
    if (filtroAtendente && atend.funcionarioNome !== filtroAtendente) {
      return false;
    }

    // Filtro por tipo
    if (filtroTipo && atend.tipoAtendimento !== filtroTipo) {
      return false;
    }

    // Filtro por período
    if (filtroPeriodo === 'hoje') {
      const hoje = new Date().toLocaleDateString('en-CA');
      if (atend.dataAtendimento !== hoje) {
        return false;
      }
    } else if (filtroPeriodo === 'semana') {
      const hoje = new Date();
      const umaSemanaAtras = new Date(hoje.getTime() - 7 * 24 * 60 * 60 * 1000);
      const dataAtend = new Date(atend.dataAtendimento);
      if (dataAtend < umaSemanaAtras) {
        return false;
      }
    } else if (filtroPeriodo === 'personalizado') {
      // Filtro por período personalizado
      const dataAtend = atend.dataAtendimento;

      if (dataInicio && dataAtend < dataInicio) {
        return false;
      }

      if (dataFim && dataAtend > dataFim) {
        return false;
      }
    }

    return true;
  });

  // Obter lista única de atendentes
  const atendentes = Array.from(new Set(atendimentos.map(a => a.funcionarioNome))).sort();

  // Obter lista única de tipos de atendimento
  const tiposAtendimento = Array.from(new Set(atendimentos.map(a => a.tipoAtendimento))).sort();

  // Separar manhã e tarde
  const atendimentosManha = atendimentosFiltrados.filter(a => {
    const hora = parseInt(a.horaInicio.split(':')[0]);
    return hora < 12;
  });

  const atendimentosTarde = atendimentosFiltrados.filter(a => {
    const hora = parseInt(a.horaInicio.split(':')[0]);
    return hora >= 12;
  });

  return (
    <div>
      {/* 🎯 Filtros Modernos */}
      <div className="filtros-container">
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Atendente</Form.Label>
              <Form.Select
                value={filtroAtendente}
                onChange={(e) => setFiltroAtendente(e.target.value)}
              >
                <option value="">Todos os atendentes</option>
                {atendentes.map(atendente => (
                  <option key={atendente} value={atendente}>{atendente}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Tipo de Atendimento</Form.Label>
              <Form.Select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
              >
                <option value="">Todos os tipos</option>
                {tiposAtendimento.map(tipo => (
                  <option key={tipo} value={tipo}>{getTipoAtendimentoLabel(tipo)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Período</Form.Label>
              <Form.Select
                value={filtroPeriodo}
                onChange={(e) => {
                  setFiltroPeriodo(e.target.value);
                  // Limpar datas personalizadas ao mudar para outro período
                  if (e.target.value !== 'personalizado') {
                    setDataInicio('');
                    setDataFim('');
                  }
                }}
              >
                <option value="hoje">Hoje</option>
                <option value="semana">Última Semana</option>
                <option value="personalizado">Período Personalizado</option>
                <option value="todos">Todos</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Campos de Data Personalizados */}
        {filtroPeriodo === 'personalizado' && (
          <Row className="mt-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-2">
                  <Calendar size={16} />
                  Data Início
                </Form.Label>
                <Form.Control
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  max={dataFim || undefined}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-2">
                  <Calendar size={16} />
                  Data Fim
                </Form.Label>
                <Form.Control
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  min={dataInicio || undefined}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        {(filtroAtendente || filtroTipo || filtroPeriodo !== 'hoje' || dataInicio || dataFim) && (
          <div className="mt-3">
            <button
              className="atendimento-btn atendimento-btn-outline"
              style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
              onClick={() => {
                setFiltroAtendente('');
                setFiltroTipo('');
                setFiltroPeriodo('hoje');
                setDataInicio('');
                setDataFim('');
              }}
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Resumo */}
      <div className="mb-3">
        <p className="text-muted mb-0">
          Mostrando <strong>{atendimentosFiltrados.length}</strong> atendimento(s)
          {filtroPeriodo === 'hoje' && ' de hoje'}
          {filtroPeriodo === 'semana' && ' da última semana'}
          {filtroPeriodo === 'personalizado' && dataInicio && dataFim &&
            ` de ${new Date(dataInicio + 'T00:00:00').toLocaleDateString('pt-BR')} até ${new Date(dataFim + 'T00:00:00').toLocaleDateString('pt-BR')}`}
          {filtroPeriodo === 'personalizado' && dataInicio && !dataFim &&
            ` a partir de ${new Date(dataInicio + 'T00:00:00').toLocaleDateString('pt-BR')}`}
          {filtroPeriodo === 'personalizado' && !dataInicio && dataFim &&
            ` até ${new Date(dataFim + 'T00:00:00').toLocaleDateString('pt-BR')}`}
        </p>
      </div>

      {/* ☀️ Parte da Manhã */}
      {atendimentosManha.length > 0 && (
        <div className="mb-4">
          <h5 className="mb-3 fw-bold d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span style={{ fontSize: '1.5rem' }}><Sun size={24} /></span>
            PARTE DA MANHÃ
          </h5>
          <div className="table-responsive">
            <Table className="modern-table" bordered hover size="sm">
              <thead>
                <tr>
                  <th>Atendente</th>
                  {/* <th>Tipo</th> */}
                  <th>Cliente</th>
                  <th>Tempo</th>
                  <th>Status</th>
                  <th>Observações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {atendimentosManha.map(atend => (
                  <tr key={atend.id}>
                    <td className="fw-semibold">{atend.funcionarioNome.toUpperCase()}</td>
                    {/* <td><small>{getTipoAtendimentoLabel(atend.tipoAtendimento)}</small></td> */}
                    <td>{atend.clienteNome}</td>
                    <td className="text-center">
                      <div className="small text-muted">Início</div>
                      <div className="fw-semibold">{atend.horaInicio}</div>
                      <div className="small text-muted">Fim</div>
                      <div className="fw-semibold">{atend.horaFim || '-'}</div>
                    </td>
                    <td>{getStatusBadge(atend.status)}</td>
                    <td>
                      <small>
                        {atend.resultado && <><strong>{getResultadoLabel(atend.resultado)}</strong><br /></>}
                        {atend.observacoes}
                      </small>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditAtendimento(atend)}
                        title="Editar atendimento"
                      >
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* 🌆 Parte da Tarde */}
      {atendimentosTarde.length > 0 && (
        <div>
          <h5 className="mb-3 fw-bold d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span style={{ fontSize: '1.5rem' }}><Sunset size={24} /></span>
            PARTE DA TARDE
          </h5>
          <div className="table-responsive">
            <Table className="modern-table" bordered hover size="sm">
              <thead>
                <tr>
                  <th>Atendente</th>
                  {/* <th>Tipo</th> */}
                  <th>Cliente</th>
                  <th>Tempo</th>
                  <th>Status</th>
                  <th>Observações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {atendimentosTarde.map(atend => (
                  <tr key={atend.id}>
                    <td className="fw-semibold">{atend.funcionarioNome.toUpperCase()}</td>
                    {/* <td><small>{getTipoAtendimentoLabel(atend.tipoAtendimento)}</small></td> */}
                    <td>{atend.clienteNome}</td>
                    <td className="text-center">
                      <div className="small text-muted">Início</div>
                      <div className="fw-semibold">{atend.horaInicio}</div>
                      <div className="small text-muted">Fim</div>
                      <div className="fw-semibold">{atend.horaFim || '-'}</div>
                    </td>
                    <td>{getStatusBadge(atend.status)}</td>
                    <td>
                      <small>
                        {atend.resultado && <><strong>{getResultadoLabel(atend.resultado)}</strong><br /></>}
                        {atend.observacoes}
                      </small>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleEditAtendimento(atend)}
                        title="Editar atendimento"
                      >
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Nenhum atendimento */}
      {atendimentosFiltrados.length === 0 && !loadingMore && (
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <p style={{ fontSize: '1.1rem', margin: 0 }}>Nenhum atendimento encontrado</p>
        </div>
      )}

      {/* Botão Carregar Mais */}
      {hasMore && atendimentosFiltrados.length > 0 && (
        <div className="text-center mt-4 mb-3">
          <button
            className="atendimento-btn atendimento-btn-primary"
            onClick={loadMore}
            disabled={loadingMore}
            style={{ minWidth: '200px' }}
          >
            {loadingMore ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Carregando...
              </>
            ) : (
              <>
                <ChevronDown size={20} className="me-2" />
                Carregar Mais Atendimentos
              </>
            )}
          </button>
          <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>
            Mostrando {atendimentosFiltrados.length} atendimento(s)
          </p>
        </div>
      )}

      {/* Indicador de loading inicial */}
      {loadingMore && atendimentosFiltrados.length === 0 && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Carregando atendimentos...</p>
        </div>
      )}

      {/* Modal de Editar Atendimento */}
      <AtendimentoForm
        key={`edit-${atendimentoParaEditar?.id}-${modalKey}`}
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setAtendimentoParaEditar(null);
        }}
        onSave={handleSaveEditAtendimento}
        funcionarios={funcionarios}
        modoEdicao={true}
        atendimentoParaEditar={atendimentoParaEditar || undefined}
      />
    </div>
  );
};

export default AtendimentoHistorico;

